// src/utils/impersonation.ts
// Downward impersonation (GitLab-style): a HotelAdmin/GroupAdmin/SuperAdmin browses the whole
// app as a lower-role user. The swap happens server-side per request (the API validates the
// REAL token's role and replaces the principal), so the UI only keeps which subject to act as
// and sends it as a header; it never mints or stores another user's token. Session-scoped on
// purpose: impersonation is a per-tab support action and must not leak into other tabs or
// survive the browser closing.

import type { UserRole } from '@/types/models';

export const IMPERSONATION_HEADER = 'X-Impersonate-Subject';

const STORAGE_KEY = 'impersonation';

/**
 * Strict hierarchy for impersonation purposes only — independent from any other ordering in
 * the app. Higher number = more authority. Roles absent from this map (e.g. "Developer", a
 * Keycloak-only persona with no seat in the local Users table) can neither impersonate nor be
 * impersonated.
 */
const ROLE_RANK: Partial<Record<UserRole, number>> = {
    Receptionist: 1,
    HotelAdmin: 2,
    GroupAdmin: 3,
    SuperAdmin: 4,
};

/** Only HotelAdmin and above may impersonate — mirrors the Users screen's own visibility gate. */
const MINIMUM_ACTOR_RANK = ROLE_RANK.HotelAdmin as number;

export interface ImpersonationState {
    /** The target user's local id (matches the API's X-Impersonate-Subject value). */
    sub: string;
    /** What the app-bar pill shows. */
    displayName: string;
    /** The target's role — becomes the effective role app-wide. */
    role: UserRole;
}

export const getImpersonation = (): ImpersonationState | null => {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
        return JSON.parse(raw) as ImpersonationState;
    } catch {
        return null;
    }
};

export const startImpersonation = (state: ImpersonationState): void => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

export const stopImpersonation = (): void => {
    sessionStorage.removeItem(STORAGE_KEY);
};

export interface ImpersonationCandidate {
    id: string;
    role: UserRole;
    isActive: boolean;
}

/**
 * Mirror of the API's guard (strictly downward, enabled, never self, never nested) so the
 * button only shows where the request would actually succeed.
 */
export const canImpersonate = (
    actorRole: UserRole | null | undefined,
    actorId: string | null | undefined,
    target: ImpersonationCandidate
): boolean => {
    if (getImpersonation()) return false;
    if (!actorRole) return false;
    const actorRank = ROLE_RANK[actorRole] ?? 0;
    if (actorRank < MINIMUM_ACTOR_RANK) return false;
    if (!target.isActive) return false;
    if (target.id === actorId) return false;
    const targetRank = ROLE_RANK[target.role] ?? 0;
    return targetRank > 0 && targetRank < actorRank;
};
